<template>
  <div class="confirmation-step">
    <tabbed-step
      :name="stepHeading"
      :loaded="!loading"
      :no-header="inSubStep"
      :button-class="buttonClass"
      hide-back-to-home
      valid
      no-footer
      hide-unloaded
      @currentStep="onCurrentStep">
      <template #status="{step}">
        <step-status :step="step"></step-status>
      </template>

      <template #loading>
        <slot name="loading" />
      </template>

      <template #default>
        <state-manager v-model="currentState">
          <state-wrapper #default="{setState}">
            <div class="confirmation-step__content">
              <h2 class="confirmation-step__heading">
                Thank you for your order
              </h2>

              <div class="alert alert-success"
                   style="margin-top: 20px;">
                <div class="orderNumberHighlight">
                  Your order has been successful, and your order number is
                  <strong>{{ orderReference }}</strong>.
                </div>
              </div>

              <slot />

              <div class="confirmation-step__footer">
                <button
                  v-if="showAssignEvents"
                  :class="style.getButtonClass('confirmation-step__assign-button')"
                  @click="setState('assign-events')">
                  {{ assignEventsButtonText }}
                </button>

                <button
                  v-if="showAssignSubscriptions"
                  :class="style.getButtonClass('confirmation-step__assign-button')"
                  @click="setState('assign-subscriptions')">
                  {{ assignSubscriptionsButtonText }}
                </button>

                <a
                  v-if="orderHistoryText && orderHistoryLink"
                  :class="style.getButtonClass('confirmation-step__order-history')"
                  :href="orderHistoryLink">
                  {{ orderHistoryText }}
                </a>

                <a
                  v-if="!hideBackToHome"
                  :class="style.getButtonClass('confirmation-step__back')"
                  href="/">
                  {{ backToHomeText }}
                </a>
              </div>
            </div>
          </state-wrapper>

          <state-wrapper v-if="isCurrentStep"
                         name="assign-events"
                         use-show
                         #default="{goBack}">
            <events-manager
              :default-org-id="webAccountOrgId"
              :hide-pricing-schedule-text="isPricingScheduleTextHidden"
              :button-class="buttonClass"
              :order-header-id="orderHeaderId"
              :user-type="userType"
              @count="(val) => internalEventsCount = val"></events-manager>

            <button :class="style.getButtonClass('confirmation-step__back')"
                    @click="goBack">
              Back to confirmation
            </button>

            <a
              v-if="orderHistoryText && orderHistoryLink"
              :class="style.getButtonClass('confirmation-step__order-history')"
              :href="orderHistoryLink">
              {{ orderHistoryText }}
            </a>

            <a
              v-if="!hideBackToHome"
              :class="style.getButtonClass('confirmation-step__back')"
              href="/">
              {{ backToHomeText }}
            </a>
          </state-wrapper>

          <state-wrapper v-if="isCurrentStep"
                         name="assign-subscriptions"
                         use-show
                         #default="{goBack}">
            <slot name="subscription-manager">
              <subscriptions-manager
                heading="Assign Subscriptions"
                :button-class="buttonClass"
                :order-header-id="orderHeaderId"
                :new-order-id="newOrderId"
                :user-type="userType"
                @count="(val) => internalSubscriptionsCount = val">
                <template #loading>
                  <slot name="loading" />
                </template>
              </subscriptions-manager>
            </slot>
            <button :class="style.getButtonClass('confirmation-step__back')"
                    @click="goBack">
              Back to confirmation
            </button>

            <a
              v-if="orderHistoryText && orderHistoryLink"
              :class="style.getButtonClass('confirmation-step__order-history')"
              :href="orderHistoryLink">
              {{ orderHistoryText }}
            </a>

            <a
              v-if="!hideBackToHome"
              :class="style.getButtonClass('confirmation-step__back')"
              href="/">
              {{ backToHomeText }}
            </a>
          </state-wrapper>
        </state-manager>
      </template>
    </tabbed-step>
  </div>
</template>

<script>
import {
  StateManager,
  StateWrapper,
  SubscriptionsManager,
  EventsManager,
  StyleMixin,
} from "spiritweb.packages.web-account";
import HttpService from "~/Scripts/services/httpService";

export default {
  name: "ConfirmationStep",
  components: {
    StateManager,
    StateWrapper,
    SubscriptionsManager,
    EventsManager,
  },
  mixins: [StyleMixin],
  props: {
    assignEvents: {
      type: Boolean,
      default: false,
    },
    assignSubscriptions: {
      type: Boolean,
      default: false,
    },
    hideBackToHome: {
      type: Boolean,
      default: false,
    },
    backToHomeText: {
      type: String,
      default: "Back to home",
    },
    orderHistoryLink: {
      type: String,
      default: null,
    },
    orderHistoryText: {
      type: String,
      default: null,
    },
    webAccountOrgId: {
      type: String,
      default: null,
    },
    assignEventsButtonText: {
      type: String,
      default: "Assign Events",
    },
    assignSubscriptionsButtonText: {
      type: String,
      default: "Assign Subscriptions",
    },
    hidePricingScheduleText: {
      type: Boolean,
      default: false,
    },
    orderHeaderId: {
      type: String,
      default: null,
    },
    subscriptionsCount: {
      type: Number,
      default: null,
    },
    eventsCount: {
      type: Number,
      default: null,
    },
    userType: {
      type: Object,
      default: () => ( {} ),
    },
  },
  data: () => ( {
    orderReference: null,
    newOrderId: null,
    loading: false,
    currentState: "default",
    internalSubscriptionsCount: 0,
    internalEventsCount: 0,
    isCurrentStep: false,
  } ),
  computed: {
    showAssignSubscriptions () {
      return this.assignSubscriptions && this.internalSubscriptionsCount > 0;
    },
    showAssignEvents () {
      return this.assignEvents && this.internalEventsCount > 0;
    },
    inSubStep () {
      return this.currentState === "assign-events" || this.currentState === "assign-subscriptions";
    },
    stepHeading () {
      let result = "Confirmation";

      if ( this.currentState === "assign-events" || this.currentState === "assign-subscriptions" ) {
        result = null;
      }

      return result;
    },
    isPricingScheduleTextHidden () {
      let result = false;
      const { eventsManager } = this.nested;

      if ( eventsManager && eventsManager.props ) {
        const {
          props: { hidePricingScheduleText },
        } = eventsManager;
        result = hidePricingScheduleText || this.hidePricingScheduleText;
      }

      return result;
    },
  },

  watch: {
    subscriptionsCount: {
      immediate: true,
      handler ( val ) {
        this.internalSubscriptionsCount = val;
      },
    },
    eventsCount: {
      immediate: true,
      handler ( val ) {
        this.internalEventsCount = val;
      },
    },
  },

  inject: {
    checkout: {
      default () {
        throw new Error( "A step must be within a <checkout>" );
      },
    },
    nested: {
      default () {
        return {
          eventsManager: {
            props: {
              hidePricingScheduleText: false,
            },
          },
        };
      },
    },
  },

  methods: {
    async onCurrentStep () {
      this.loading = true;
      try {
        const confInstance = new HttpService( "umbraco/api/GlConfirmationStep/" );
        const response = await confInstance.get( "GetConfirmedOrder" );

        let items = [];
        if ( response && response.data ) {
          if ( response.data.sopLines && response.data.sopLines.length > 0 ) {
            const gtagInstance = new HttpService( "umbraco/api/Gtag/" );
            const eventDataResponse = await gtagInstance.post( "GetEventData",
              response.data.sopLines.map( l => ( {
                id: l.productId,
                price: l.itemPrice,
                quantity: l.quantity,
              } ) ),
            );

            if ( eventDataResponse && eventDataResponse.data ) {
              items = eventDataResponse.data;
            }
          }

          this.$root.$emit( "sw::clearBasket::request" );

          this.orderReference = response.data.reference;
          this.newOrderId = response.data.id;

          this.$gtag.onEvent( { ecommerce: null } ); // Clear the previous ecommerce object
          this.$gtag.onEvent( {
            event: "ecomPurchase",
            ecommerce: {
              purchase: {
                actionField: {
                  id: this.orderReference,
                  revenue: response.data.totalExcludingVat,
                  step: this.checkout.currentStepIndex + 1,
                },
                products: items.map( this.$gtag.parseAdditionalData ),
              },
            },
          } );
        }
      } catch ( error ) {
        console.log( "error", error );
      } finally {
        this.loading = false;
        this.isCurrentStep = true;
      }

      this.$emit( "submit", true );
    },
  },
};
</script>

<style lang="less" scoped>
.confirmation-step {
  &__content {
    max-width: 600px;
  }

  &__heading {
    font-size: 18pt;
  }

  .step__continue {
    margin-top: 30px;
    display: inline-block;
  }
}
</style>
